import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, throwError } from 'rxjs';
import { catchError, concatMap, map, switchMap, withLatestFrom } from 'rxjs/operators';
import {
    ContractActionTypes,
    DeleteContractResponse,
    GetContractsResponse,
    PatchContractResponse,
    PostContractResponse,
} from '../../actions/mdqx/contract.actions';
import { stringifyIfNotNullOrUndefined } from '../../../../helper/helper';
import { HttpService } from '../../../services/http.service';
import { ContractEntity } from '../../../entities/mdqx/contract.entity';
import { DaveActions } from '../../actions/actions';
import { getToken } from '../../selectors/base.selectors';
import { BaseActionTypes } from '../../actions/base.actions';
import { State } from '../../index';
import { environment } from '../../../../../environments/environment';


enum ErrorCodes {
    Get = 'Verträge Abrufen fehlgeschlagen',
    Post = 'Vertrag Anlegen fehlgeschlagen',
    Patch = 'Vertrag Bearbeiten fehlgeschlagen',
    Delete = 'Vertrag Löschen fehlgeschlagen',
}

@Injectable()
export class ContractEffects {
    
    get$ = createEffect(() => this.actions$.pipe(
        ofType(ContractActionTypes.GetContractsRequest),
        map(
            () => `
            {
                contract {
                    ${ContractEntity.GqlFields}
                }
            }`,
        ),
        withLatestFrom(this.store.select(getToken)),
        switchMap(([query, token]) =>
            this.api.graphQl({ query }, { token }, '', environment.mdqxGatewayApiUrl).pipe(
                map(res => res && res.contract),
                concatMap(res =>
                    res
                        ? [
                              new GetContractsResponse(
                                  res.map(contract => new ContractEntity(contract)),
                              ),
                          ]
                        : throwError('empty response'),
                ),
                catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: ErrorCodes.Get,
                        Err: err,
                        Caught: caught,
                    }
                }))),
                ),
        ),
    ));

    
    post$ = createEffect(() => this.actions$.pipe(
        ofType(ContractActionTypes.PostContractRequest),
        map(
            ({ Payload }) => `
            mutation {
                createContract(
                    ${stringifyIfNotNullOrUndefined(Payload, 'abrechnungszyklus')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'vertragsNummer')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'contractGroupTypeId')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'contractSign')}
                    contractStatusId: ${JSON.stringify(Payload.contractStatusId)}
                    ${stringifyIfNotNullOrUndefined(Payload, 'description')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'endDate')}
                    name: ${JSON.stringify(Payload.name)}
                    ${stringifyIfNotNullOrUndefined(Payload, 'number')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'startDate')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'sachkontoDebitor')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'sachkontoKreditor')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'hatDiagnostik')}
                ) {
                    ${ContractEntity.GqlFields}
                }
            }`,
        ),
        withLatestFrom(this.store.select(getToken)),
        concatMap(([query, token]) =>
            this.api.graphQl({ query }, { token }, '', environment.mdqxGatewayApiUrl).pipe(
                map(res => res && res.createContract),
                concatMap(res =>
                    res
                        ? [new PostContractResponse(new ContractEntity(res))]
                        : throwError('empty response'),
                ),
                catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: ErrorCodes.Post,
                        Err: err,
                        Caught: caught,
                    }
                }))),            ),
        ),
    ));

    
    patch$ = createEffect(() => this.actions$.pipe(
        ofType(ContractActionTypes.PatchContractRequest),
        map(
            ({ Payload }) => `
            mutation {
                changeContract(
                    ${stringifyIfNotNullOrUndefined(Payload, 'abrechnungszyklus')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'vertragsNummer')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'contractGroupTypeId')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'contractSign')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'contractStatusId')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'description')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'endDate')}
                    id: ${JSON.stringify(Payload.id)}
                    ${stringifyIfNotNullOrUndefined(Payload, 'name')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'number')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'startDate')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'sachkontoDebitor')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'sachkontoKreditor')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'hatDiagnostik')}
                ) {
                    ${ContractEntity.GqlFields}
                }
            }`,
        ),
        withLatestFrom(this.store.select(getToken)),
        concatMap(([query, token]) =>
            this.api.graphQl({ query }, { token }, '', environment.mdqxGatewayApiUrl).pipe(
                map(res => res && res.changeContract),
                concatMap(res =>
                    res
                        ? [new PatchContractResponse(new ContractEntity(res))]
                        : throwError('empty response'),
                ),
                catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: ErrorCodes.Patch,
                        Err: err,
                        Caught: caught,
                    }
                }))),            ),
        ),
    ));

    
    delete$ = createEffect(() => this.actions$.pipe(
        ofType(ContractActionTypes.DeleteContractRequest),
        withLatestFrom(this.store.select(getToken)),
        concatMap(([{ Payload }, token]) =>
            this.api
                .graphQl(
                    {
                        query: `
                        mutation {
                            deleteContract(id: ${JSON.stringify(Payload.id)})
                        }`,
                    },
                    { token }, '', environment.mdqxGatewayApiUrl
                )
                .pipe(
                    map(res => res && res.deleteContract),
                    concatMap(res =>
                        res
                            ? [new DeleteContractResponse(Payload.id)]
                            : throwError('falsy response'),
                    ),
                    catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: ErrorCodes.Delete,
                            Err: err,
                            Caught: caught,
                        }
                    }))),                ),
        ),
    ));

    constructor(
        private actions$: Actions<DaveActions>,
        private api: HttpService,
        private store: Store<State>,
    ) {}
}
